import * as React from "react";
import { NavLink } from "react-router";
import { GraduationCap, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "../../lib/cn.js";
import type { NavGroup } from "../navigation/nav-types.js";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip.js";
import { Button } from "../ui/button.js";

export const SIDEBAR_COLLAPSED_STORAGE_KEY = "academia-hub-sidebar-collapsed";

export interface SidebarProps {
  groups: NavGroup[];
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  className?: string;
}

/**
 * Desktop primary navigation, per `17_UI_VISUAL_DESIGN_SYSTEM.md` §2.2: grouped, collapsible,
 * one icon family, labels expanded / tooltips collapsed, at most two visible hierarchy levels
 * (group label, then items — no third level).
 */
export function Sidebar({ groups, collapsed, onCollapsedChange, className }: SidebarProps) {
  return (
    <aside
      className={cn(
        "hidden shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 md:flex",
        collapsed ? "w-[72px]" : "w-[264px]",
        className,
      )}
    >
      <div className={cn("flex h-16 shrink-0 items-center border-b border-sidebar-border px-4", collapsed && "justify-center px-0")}>
        <div className="flex items-center gap-2 overflow-hidden">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-primary text-primary-foreground">
            <GraduationCap className="size-[18px]" />
          </span>
          {!collapsed && <span className="truncate text-sm font-semibold tracking-tight text-foreground">Academia HUB</span>}
        </div>
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-4" aria-label="Primary">
        {groups.map((group) => (
          <div key={group.label} className="space-y-1">
            {!collapsed && <p className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</p>}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.href}>
                  <SidebarLink item={item} collapsed={collapsed} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="shrink-0 border-t border-sidebar-border p-2">
        <Button
          variant="ghost"
          size={collapsed ? "icon" : "md"}
          className={cn("w-full justify-start gap-2 text-sidebar-foreground hover:bg-sidebar-active/60", collapsed && "justify-center")}
          onClick={() => onCollapsedChange(!collapsed)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          {!collapsed && <span>Collapse</span>}
        </Button>
      </div>
    </aside>
  );
}

function SidebarLink({ item, collapsed }: { item: NavGroup["items"][number]; collapsed: boolean }) {
  const link = (
    <NavLink
      to={item.href}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-[var(--radius-control)] px-2.5 py-2 text-sm font-medium transition-colors",
          "hover:bg-sidebar-active/60",
          isActive ? "bg-sidebar-active text-sidebar-active-foreground" : "text-sidebar-foreground",
          collapsed && "justify-center px-0",
        )
      }
      end={item.href === "/"}
    >
      <item.icon className="size-[18px] shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && item.badge && (
        <span className="ml-auto rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">{item.badge}</span>
      )}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}
